import "./ProjectorView.scss";

import clsx from "clsx";
import { FC, useCallback, useEffect, useRef } from "react";

import { blockIsAsset, hasEditableBlocks } from "../../../../shared/worksheets/doc-helpers";

import { ProjectorHeader } from "./ProjectorHeader";

import { SERVER_API_PATH } from "api/api-constants";
import { ErrorScreen, LoadingScreen } from "components/LoadingScreen";
import { PageDetailView } from "components/materials/page/content/PageDetailView";
import { FeatureProjectorView } from "components/materials/presentation/featuring/FeatureProjectorView";
import {
  PROJECTOR_ID_KEY,
  PageScrollMessage,
  RequestFrameMessage,
  SyncClassSessionMessage,
  WindowFrameMessage,
  WindowMessages,
  addProjectorMessageListener,
  confirmCorrectProjectorView,
  deregisterProjectorView,
  getScrollTopFromMessage,
  removeProjectorMessageListener,
  sendMessageToInstructorView,
} from "components/materials/presentation/projector/projector-messaging";
import { PageOutcomeScore } from "components/outcomes/PageOutcomeScore";
import { t } from "i18n/i18n";
import { useFetchGroupData } from "mds/hooks/use-fetch-group-data";
import { useWorksheet } from "sharedb/ShareDBDoc";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { pageSelectors, selectNestedPageOutcomes } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { getCsrfToken } from "utils/auth";
import { FeatureStatus, usePresentation, useProjectorScalingFactor } from "utils/presentation";

// Featuring seems to be about 8 pixels off.
const WAIT_FOR_RENDER_INTERVAL_MS = 500;
const RESIZE_EVENT_DELAY_MS = 600;

// This page is meant to work in conjunction with the ProjectorScroller component.  It opens as a new
// tab and sends back the scroll frame and the total height of the page to the parent window.  The
// parent window then resizes the div container to match the size of this frame (taking into account
// the 20% less real estate on the div container vs this frame) so that the content in both scrolling
// windows can match perfectly.  This allows for instructors to see exactly the content they are scrolling
// into view without having to look at the projector screen.
export const ProjectorView: FC = () => {
  const { canAuthorSection } = useCapabilities();
  const presentationDivRef = useRef<HTMLDivElement>(null);
  const featureDivRef = useRef<HTMLDivElement>(null);
  const lastScrollMessageRef = useRef<PageScrollMessage | null>(null);

  const {
    presentedSessionId,
    presentedPageId: pageId,
    presentedBlockId,
    presentingRollup,
    presentedAccessId,
    hasProjectorView,
    featureStatus,
  } = usePresentation();
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["id", "interaction_mode", "worksheet_id"],
  );
  const pageOutcomes = useAppSelector((s) => selectNestedPageOutcomes(s, pageId));
  const isFeaturing = featureStatus !== FeatureStatus.NONE;
  const scalingFactor = useProjectorScalingFactor();

  const { doc } = useWorksheet(page?.worksheet_id);
  const isStaticContent = !doc || !hasEditableBlocks(doc);
  const isFeaturingAsset = Boolean(isFeaturing && doc && blockIsAsset(doc, presentedBlockId));

  const interactionMode = page?.interaction_mode;

  const getScrollerDiv = useCallback(
    () => (isFeaturing ? featureDivRef.current : presentationDivRef.current),
    [isFeaturing],
  );

  const updateScrollPosition = useCallback(() => {
    const scrollerDiv = getScrollerDiv();
    if (!scrollerDiv || !lastScrollMessageRef.current || !hasProjectorView) {
      return;
    }

    if (lastScrollMessageRef.current.scrollY === 0 || !lastScrollMessageRef.current.blockMarkerId) {
      scrollerDiv.scroll({
        left: lastScrollMessageRef.current.scrollX * scalingFactor,
        top: lastScrollMessageRef.current.scrollY * scalingFactor,
        behavior: "smooth",
      });

      return;
    }

    if (lastScrollMessageRef.current.hasScrolledToBottom) {
      scrollerDiv.scrollTo({ top: scrollerDiv.scrollHeight, behavior: "smooth" });
      return;
    }

    const scrollY = getScrollTopFromMessage(
      scrollerDiv,
      lastScrollMessageRef.current,
      scalingFactor,
    );
    const scrollX = lastScrollMessageRef.current.scrollX * scalingFactor;

    // Scroll our window to match what the instructor is scrolling
    scrollerDiv.scroll({
      left: scrollX,
      top: scrollY,
      behavior: "smooth",
    });
  }, [hasProjectorView, getScrollerDiv, scalingFactor]);

  const postFrameMessage = useCallback(() => {
    const scrollerDiv = getScrollerDiv();
    if (hasProjectorView && scrollerDiv) {
      const assetDiv = scrollerDiv.querySelector<HTMLDivElement>(".asset-container");
      const message: WindowFrameMessage = {
        type: WindowMessages.WINDOW_FRAME,
        viewport: {
          width: scrollerDiv.offsetWidth,
          height: scrollerDiv.offsetHeight,
        },
        assetContainer: assetDiv
          ? {
              width: assetDiv.offsetWidth,
              height: assetDiv.offsetHeight,
              hasHorizontalScrollbar: assetDiv.scrollWidth > assetDiv.clientWidth,
              hasVerticalScrollbar: assetDiv.scrollHeight > assetDiv.clientHeight,
            }
          : undefined,
      };

      sendMessageToInstructorView(message);
      updateScrollPosition();
    }
  }, [hasProjectorView, updateScrollPosition, getScrollerDiv]);

  // Load the page groups, page section users depending on the type of page
  useFetchGroupData(pageId, interactionMode);

  // channel listeners
  useEffect(() => {
    const handleProjectorMessage = (
      message: MessageEvent<PageScrollMessage | RequestFrameMessage | SyncClassSessionMessage>,
    ) => {
      const scrollerDiv = getScrollerDiv();
      if (!scrollerDiv) {
        // We can't process anything until we have a ref to the scroller div
        return;
      }

      // TODO: Switch statement might be more appropriate here
      if (message.data.type === WindowMessages.PAGE_SCROLL) {
        lastScrollMessageRef.current = message.data;
        updateScrollPosition();
      } else if (message.data.type === WindowMessages.REQUEST_FRAME) {
        postFrameMessage();
      } else if (message.data.type === WindowMessages.SESSION_SYNC && presentedSessionId) {
        // If the instructor is presenting, we need to make sure we are in sync with them
        storeApi.class_sessions.retrieve(presentedSessionId, { skipToast: true, skipCache: true });
      }
    };

    addProjectorMessageListener(handleProjectorMessage);

    return () => {
      removeProjectorMessageListener(handleProjectorMessage);
    };
  }, [postFrameMessage, updateScrollPosition, presentedSessionId, getScrollerDiv]);

  // Reset the projecting state when the window closes
  useEffect(() => {
    const closeWindow = () => {
      if (hasProjectorView && presentedSessionId) {
        deregisterProjectorView();
        const url = `${SERVER_API_PATH}/material/class_sessions/${presentedSessionId}/stop-projecting/`;
        const formData = new FormData();
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          formData.append("csrfmiddlewaretoken", csrfToken);
        }
        navigator.sendBeacon(url, formData);
      }
    };

    window.addEventListener("beforeunload", closeWindow);

    return () => {
      window.removeEventListener("beforeunload", closeWindow);
    };
  }, [hasProjectorView, presentedSessionId]);

  // Pass the window pane dimensions to the caller window.
  // MutationObserver.observe is cleaned via disconnect(); react-doctor false-matches `.observe` as `.on`.
  // react-doctor-disable-next-line react-doctor/effect-needs-cleanup
  useEffect(() => {
    if (!hasProjectorView) {
      return;
    }

    let interval: NodeJS.Timeout | undefined;
    let mutationObserver: MutationObserver | undefined;
    let pendingResizeTimeout: NodeJS.Timeout | undefined;
    let scrollerReady = false;

    const delayedPostFrameMessage = () => {
      if (pendingResizeTimeout) {
        clearTimeout(pendingResizeTimeout);
      }
      pendingResizeTimeout = setTimeout(postFrameMessage, RESIZE_EVENT_DELAY_MS);
    };

    const handleProjectorChange = (event: StorageEvent) => {
      if (event.key === PROJECTOR_ID_KEY) {
        confirmCorrectProjectorView();
      }
    };

    // Unfortunately, we can't rely on the page being fully rendered when we start projecting
    // So we need to wait until the page is fully rendered before we can send the correct
    // dimensions to the parent window
    const checkDiv = () => {
      const scrollerDiv = getScrollerDiv();
      if (scrollerDiv && !scrollerReady) {
        scrollerReady = true;
        postFrameMessage();

        if (interval) {
          clearInterval(interval);
          interval = undefined;
        }
        mutationObserver = new MutationObserver(delayedPostFrameMessage);

        mutationObserver.observe(scrollerDiv, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }
    };

    // Set up an interval to check periodically
    confirmCorrectProjectorView();
    window.addEventListener("storage", handleProjectorChange);
    window.addEventListener("resize", delayedPostFrameMessage);
    interval = setInterval(checkDiv, WAIT_FOR_RENDER_INTERVAL_MS);

    // Clean up interval on component unmount
    return () => {
      clearInterval(interval);
      clearTimeout(pendingResizeTimeout);
      window.removeEventListener("resize", delayedPostFrameMessage);
      window.removeEventListener("storage", handleProjectorChange);
      mutationObserver?.disconnect();
    };
    // react-doctor-disable-next-line react-doctor/prefer-use-effect-event
  }, [hasProjectorView, isFeaturing, pageId, postFrameMessage, getScrollerDiv]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--projector-scaling-factor",
      scalingFactor.toString(),
    );
  }, [scalingFactor]);

  if (canAuthorSection === false) {
    return (
      <div className="h-screen w-screen">
        <ErrorScreen className="h-full">{t("section.404")}</ErrorScreen>
      </div>
    );
  }

  // If we are not in projectorView by default we show a loading screen
  if (!hasProjectorView || !page) {
    return <LoadingScreen>{t("presentation.presentation_ended")}</LoadingScreen>;
  }

  return (
    <main className="projector-view h-full bg-white">
      {page && (
        <ProjectorHeader
          accessId={presentedAccessId}
          blockId={presentedBlockId}
          doc={doc}
          featureStatus={featureStatus}
          pageId={page.id}
        />
      )}

      {isFeaturing && (
        <FeatureProjectorView
          isFeaturingAsset={isFeaturingAsset}
          isStaticContent={isStaticContent}
          ref={featureDivRef}
        />
      )}
      <div
        className={clsx("projector-view-main w-full", {
          "hide-during-featuring": isFeaturing,
        })}
        id="projector-scroller"
        ref={presentationDivRef}
      >
        {/* Spacing between header and outcomes */}
        <div className="h-[calc(60px/var(--projector-scaling-factor))]" />

        <div className="projector-view-main-content flex flex-col items-center">
          <div className="items-top page-block-width ml-1 flex flex-wrap gap-2 text-blue-tint-20">
            {pageOutcomes.map((pageOutcome) => (
              <PageOutcomeScore key={pageOutcome.id} pageOutcome={pageOutcome} />
            ))}
          </div>

          {/* Spacing between outcomes and blocks */}
          <div className="h-[calc(40px/var(--projector-scaling-factor))]" />

          {page && (
            <PageDetailView
              isStaticContent={isStaticContent}
              pageId={page.id}
              selectedAccessId={presentedAccessId}
              selectedTabVariant={presentingRollup ? "responses" : null}
              isProjecting
            />
          )}
        </div>
      </div>
    </main>
  );
};
